/**
 * @description list를 key에 따라 그룹핑하여 Map으로 반환하는 유틸 함수
 *
 * @param list 리스트
 * @param key 그룹핑할 key (리스트의 요소의 key 중 하나이어야 함)
 *
 * @returns Map<T[keyof T], T[]>
 */
export const groupByList = <T>(
  list: T[],
  key: keyof T,
): Map<T[keyof T], T[]> => {
  const map = new Map<T[keyof T], T[]>();

  for (const item of list) {
    const itemKey = item[key];
    const value = map.get(itemKey);
    if (!value) {
      map.set(itemKey, [item]);
      continue;
    }
    value.push(item);
  }

  return map;
};

// ------------------------------------------------------------

/**
 * Fisher-Yates 알고리즘을 사용하여 배열을 무작위로 섞습니다.
 * 이 알고리즘은 현재 인덱스에서 무작위로 선택된 요소와 자리를 바꾸는 방식으로 작동하여, O(n) 시간 복잡도로 배열을 섞을 수 있습니다.
 * 원본 배열을 변경하지 않고 새로운 배열을 반환합니다.
 *
 * 이 함수는 배열의 요소가 순서에 의존하지 않고 무작위로 나타나야 하는 상황에서 유용합니다.
 *
 * @param array 무작위로 섞을 배열
 * @returns 섞인 요소를 가진 새로운 배열
 *
 * 사용 방법:
 *
 * const numbers = [1, 2, 3, 4, 5];
 * const shuffledNumbers = shuffleArray(numbers);
 * console.log(shuffledNumbers); // [3, 1, 5, 4, 2]와 같이 섞인 배열이 출력됩니다.
 *
 * const strings = ["apple", "banana", "cherry"];
 * const shuffledStrings = shuffleArray(strings);
 * console.log(shuffledStrings); // ["banana", "cherry", "apple"]와 같이 섞인 배열이 출력됩니다.
 *
 * 제네릭 타입을 사용하여 다양한 타입의 배열을 섞을 수 있습니다.
 */
export function shuffleArray<T>(array: T[]): T[] {
  const shuffledArray = [...array]; // 원본 배열을 복사하여 변경하지 않도록 합니다.
  for (let i = shuffledArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1)); // 0에서 i 사이의 무작위 인덱스를 선택합니다.
    [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]]; // 요소를 교환합니다.
  }
  return shuffledArray;
}
